import { useState, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import useFetchFinancialInstitutionBranches from '@/hooks/useApi/useFetchFinancialInstitutionBranches';
import useFetchFinancialInstitutions from '@/hooks/useApi/useFetchFinancialInstitutions';
import { Select, TextField, FinancialInstitutionBranch, FinancialInstitution, FieldData } from 'vopay';

import { PaymentFieldDefault } from './types';

type FiOption = {
  id: string;
  number: string;
  label: string;
};

type FiBranchOption = {
  transitNumber: string;
  label: string;
};

export function useBankAccountFields(
  Country: string,
  clientAccountsEnabled: boolean,
): FieldData<PaymentFieldDefault>[] {
  const [fiRes, fetchFinancialInstitutions] = useFetchFinancialInstitutions({});
  const [fiBranchRes, fetchFiBranches] = useFetchFinancialInstitutionBranches({});
  const [fiOptions, setFiOptions] = useState<FiOption[]>([]);
  const [fiBranchOptions, setFiBranchOptions] = useState<FiBranchOption[]>([]);
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });

  useEffect(() => {
    fetchFinancialInstitutions({});
  }, []);

  useEffect(() => {
    if (fiRes.Success) {
      setFiOptions(Object.values(fiRes.FinancialInstitutions || {}).map(convertToFiOption));
    } else {
      setFiOptions([]);
    }
  }, [fiRes]);

  useEffect(() => {
    if (fiBranchRes.Success) {
      setFiBranchOptions(Object.values(fiBranchRes.Branches || {}).map(convertToFiBranchOption));
    } else {
      setFiBranchOptions([]);
    }
  }, [fiBranchRes]);

  function handleFiFilter(filter: string) {
    const filtered = Object.values(fiRes.FinancialInstitutions || {}).filter((fi) => {
      if (!filter) return true;

      return `${fi.FinancialInstitutionID}${
        fi.FinancialInstitutionNumber
      }${fi.FIName.toLowerCase()}${fi.FINameFrench.toLowerCase()}`.includes(filter.toLowerCase());
    });

    if (filtered.length === 0 && filter !== '' && !isNaN(Number(filter))) {
      setFiOptions([
        {
          id: filter,
          number: filter,
          label: t('manuallyEntered'),
        },
      ]);
    } else {
      setFiOptions(filtered.map(convertToFiOption));
    }
  }

  function handleFiBranchFilter(filter: string) {
    const filtered = Object.values(fiBranchRes.Branches || {}).filter((branch) => {
      if (!filter) return true;

      return `${
        branch.BranchTransitNumber
      }${branch.Address.toLowerCase()}${branch.TownCity.toLowerCase()}${branch.PostalCode.toLowerCase()}${branch.Province.toLowerCase()}`.includes(
        filter.toLowerCase(),
      );
    });

    if (filtered.length === 0 && filter !== '' && !isNaN(Number(filter))) {
      setFiBranchOptions([
        {
          transitNumber: filter,
          label: t('manuallyEntered'),
        },
      ]);
    } else {
      setFiBranchOptions(filtered.map(convertToFiBranchOption));
    }
  }

  function getFiNum(fiID: string) {
    return fiOptions.find((fi) => fi.id === fiID)?.number || fiID;
  }

  function convertToFiOption(fi: FinancialInstitution): FiOption {
    return {
      id: fi.FinancialInstitutionID,
      number: fi.FinancialInstitutionNumber,
      label: i18n.language === 'fr' ? fi.FINameFrench : fi.FIName,
    };
  }

  function convertToFiBranchOption(branch: FinancialInstitutionBranch): FiBranchOption {
    return {
      transitNumber: branch.BranchTransitNumber.toString(),
      label: [branch.Address, branch.TownCity, branch.PostalCode, branch.Province].filter((val) => !!val).join(', '),
    };
  }

  return useMemo(() => {
    return [
      {
        label: () => t('Fields.financialInstitutionNumber.label'),
        fieldName: 'FinancialInstitutionNumber',
        serializeField: (values) => (clientAccountsEnabled ? '' : getFiNum(values.FinancialInstitutionNumber) || ''),
        getInputState: () => 'required',
        showField: (values) =>
          values.PaymentSelectionMethod === 'bank' &&
          Country === 'CA' &&
          values.ProvideOrRequestPaymentInformation === 'provide' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({
            onChange: async (e) => {
              ctx.setFieldValue('BranchTransitNumber', '');
              ctx.setFieldTouched('BranchTransitNumber', false);

              const fiID = Number(e.target.value);

              if (isNaN(fiID) || !fiID) {
                setFiBranchOptions([]);
              } else if (fiID) {
                fetchFiBranches({ FinancialInstitutionID: fiID });
              }
            },
          });
          return (
            <Select
              {...fieldData}
              version="2"
              showClear
              filterable
              onFilter={(e) => {
                handleFiFilter(e.filter);
              }}
              sort={false}
              limit={20}
              options={fiOptions}
              optionValue={(o) => o.id}
              optionLabel={(o) => [o.number, o.label].filter((val) => !!val).join(' ')}
              emptyFilterMsg={t('Fields.financialInstitutionNumber.emptyMsg')}
              renderOption={(fi, { Option }) => (
                <Option>
                  {fi.number} <br /> {fi.label}
                </Option>
              )}
            />
          );
        },
        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.branchTransitNumber.label'),
        fieldName: 'BranchTransitNumber',
        serializeField: (values) => (clientAccountsEnabled ? '' : values?.BranchTransitNumber || ''),
        getInputState: (ctx) => (ctx.values.FinancialInstitutionNumber ? 'required' : 'disabled'),
        showField: (values) =>
          values.PaymentSelectionMethod === 'bank' &&
          Country === 'CA' &&
          values.ProvideOrRequestPaymentInformation === 'provide' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              {...fieldData}
              version="2"
              showClear
              filterable
              onFilter={(e) => handleFiBranchFilter(e.filter)}
              limit={20}
              options={fiBranchOptions}
              optionValue={(branch) => branch.transitNumber}
              optionLabel={(branch) => [branch.transitNumber, branch.label].filter((val) => !!val).join(' ')}
              emptyFilterMsg={t('Fields.branchTransitNumber.emptyMsg')}
              renderOption={(branch, { Option }) => (
                <Option>
                  {branch.transitNumber} <br />
                  {branch.label}
                </Option>
              )}
            />
          );
        },
        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.abaRoutingNumber.label'),
        fieldName: 'ABARoutingNumber',
        serializeField: (values) => (clientAccountsEnabled ? '' : values?.ABARoutingNumber || ''),
        showField: (values) =>
          values.PaymentSelectionMethod === 'bank' &&
          Country === 'US' &&
          values.ProvideOrRequestPaymentInformation === 'provide' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        getInputState: () => 'required',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="positiveInteger" />;
        },
        validate: () =>
          Yup.string()
            .required()
            .test('aba', t('Fields.abaRoutingNumber.validation'), (value) => {
              const match = /^\s*([\d]{9})\s*$/.exec(value || '');

              if (!match) {
                return false;
              }

              const weights = [3, 7, 1];
              const aba = match[1];

              let sum = 0;

              for (let i = 0; i < 9; ++i) {
                sum += Number(aba.charAt(i)) * weights[i % 3];
              }

              return sum !== 0 && sum % 10 === 0;
            }),
      },
      {
        label: () => t('Fields.accountNumber.label'),
        fieldName: 'AccountNumber',
        serializeField: (values) => (clientAccountsEnabled ? '' : values?.AccountNumber || ''),
        getInputState: () => 'required',
        showField: (values) =>
          values.PaymentSelectionMethod === 'bank' &&
          values.ProvideOrRequestPaymentInformation === 'provide' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="positiveInteger" />;
        },
        validate: () => Yup.string().min(4, t('Fields.accountNumber.validation')).required(),
      },
    ];
  }, [Country, fiOptions, fiBranchOptions, clientAccountsEnabled]);
}
